import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { store, type MaterialTarget } from '../../state/store';
import type { Surface } from '../../core/hp/types';
import { t } from '../../i18n/I18n';

/**
 * «Свой цвет» of the material sheet, the app's CustomColorPopup (the first tile of its colours, ColorTags → SetHexColor
 * with a new colour material): a colour by its HEX, its hue, saturation and brightness — HSL, as the app's wheel and
 * sliders hold it — and its transparency. The object wears it live while it is chosen, as a properties preview with no
 * undo step and no save (`store.previewSurface`); «Готово» keeps it as one undo step, «Отмена» or Escape lets it go.
 */
export interface Hsla { h: number; s: number; l: number; a: number }

/** The app's starting colour when the finish has none (`SetHexColor.defaultColorHex`). */
const DEFAULT_HEX = '#FDFDFD';

export function rgbOf({ h, s, l }: Hsla): [number, number, number] {
  const saturation = s / 100, lightness = l / 100, chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hue = ((h % 360) + 360) % 360 / 60, x = chroma * (1 - Math.abs(hue % 2 - 1)), m = lightness - chroma / 2;
  const [r, g, b] = hue < 1 ? [chroma, x, 0] : hue < 2 ? [x, chroma, 0] : hue < 3 ? [0, chroma, x] : hue < 4 ? [0, x, chroma] : hue < 5 ? [x, 0, chroma] : [chroma, 0, x];
  return [r + m, g + m, b + m];
}

export function hslaOf([r, g, b]: readonly number[], a = 1): Hsla {
  const max = Math.max(r, g, b), min = Math.min(r, g, b), lightness = (max + min) / 2, delta = max - min;
  if (delta < 1e-9) return { h: 0, s: 0, l: lightness * 100, a };
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  const hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
  return { h: (hue * 60 + 360) % 360, s: Math.min(100, saturation * 100), l: lightness * 100, a };
}

const byte = (value: number) => Math.round(Math.max(0, Math.min(1, value)) * 255).toString(16).padStart(2, '0').toUpperCase();
/** `#RRGGBB`, or `#RRGGBBAA` while it is see-through. */
export function hexOf(colour: Hsla): string {
  return '#' + rgbOf(colour).map(byte).join('') + (colour.a < 1 ? byte(colour.a) : '');
}

/** The colour a HEX names (`#RGB`, `#RRGGBB`, `#RRGGBBAA`, the hash optional), or null. */
export function hslaOfHex(text: string): Hsla | null {
  const hex = text.trim().replace(/^#/, '');
  if (!/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) return null;
  const full = hex.length === 3 ? [...hex].map(c => c + c).join('') : hex;
  const channels = [0, 2, 4, 6].slice(0, full.length / 2).map(i => parseInt(full.slice(i, i + 2), 16) / 255);
  return hslaOf(channels.slice(0, 3), channels[3] ?? 1);
}

/** The finish the colour makes: a painted colour, see-through below full opacity, as the app's colour material. */
export function customSurface(colour: Hsla): Surface {
  const surface: Surface = { color: rgbOf(colour), type: 'mtl_paint', caption: t('#CustomColourTitle') };
  if (colour.a < 1) surface.opacity = Math.round(colour.a * 1000) / 1000;
  return surface;
}

const css = (colour: Hsla) => `hsl(${colour.h} ${colour.s}% ${colour.l}%)`;

export function CustomColour({ target, start, onDone, onCancel }: {
  target: NonNullable<MaterialTarget>; start?: Surface; onDone: (surface: Surface) => void; onCancel: () => void;
}) {
  const owner = useMemo(() => ({}), []);
  const [colour, setColour] = useState<Hsla>(() => start?.color && !start.texture && !start.customTexture
    ? hslaOf(start.color, start.opacity ?? 1) : hslaOfHex(DEFAULT_HEX)!);
  const [hexText, setHexText] = useState<string | null>(null);
  const hexRef = useRef<HTMLInputElement>(null);
  const surface = customSurface(colour), key = JSON.stringify(surface);
  // The object wears the colour at most once a frame; leaving takes the look away again unless it was kept.
  useEffect(() => {
    const frame = requestAnimationFrame(() => store.previewSurface(target, surface, owner));
    return () => cancelAnimationFrame(frame);
  }, [key]);
  useEffect(() => () => store.clearPropertiesPreview(owner), [owner]);
  useEffect(() => { hexRef.current?.focus({ preventScroll: true }); }, []);
  const change = (patch: Partial<Hsla>) => { setHexText(null); setColour(value => ({ ...value, ...patch })); };
  const done = () => { store.clearPropertiesPreview(owner); onDone(surface); };
  const cancel = () => { store.clearPropertiesPreview(owner); onCancel(); };
  const typedHex = hexText ?? hexOf(colour), hexValid = !!hslaOfHex(typedHex);
  const slider = (name: keyof Hsla, label: string, max: number, unit: string, track: string, value = colour[name], set = (next: number) => change({ [name]: next })) =>
    <label className="colour-control" key={name}><span>{label}</span>
      <input className="texture-slider colour-slider" type="range" min={0} max={max} step={1} value={Math.round(value)} aria-label={label}
        aria-valuetext={`${Math.round(value)}${unit}`} style={{ '--colour-track': track } as CSSProperties} onChange={event => set(Number(event.target.value))} />
      <output aria-hidden="true">{Math.round(value)}{unit}</output></label>;
  return <div className="custom-colour" role="group" aria-label={t('#CustomColourTitle')} onKeyDown={event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); cancel(); }
  }}>
    {/* As the app's popup: «Отмена» on the left, «Готово» on the right, always in view. */}
    <div className="custom-colour-actions">
      <button type="button" className="text-button" onClick={cancel}>{t('Cancel')}</button>
      <strong>{t('#CustomColourTitle')}</strong>
      <button type="button" className="custom-colour-done" onClick={done} disabled={!hexValid}>{t('Done')}</button>
    </div>
    <div className="custom-colour-head">
      <span className="custom-colour-preview" style={{ '--colour': css(colour), opacity: Math.max(colour.a, .08) } as CSSProperties} aria-hidden="true" />
      <label className="custom-colour-hex"><span>HEX</span>
        <input ref={hexRef} value={typedHex} aria-label={t('#CustomColourHex')} aria-invalid={!hexValid} spellCheck={false} autoComplete="off" maxLength={9}
          onChange={event => { const text = event.target.value; setHexText(text); const parsed = hslaOfHex(text); if (parsed) setColour(parsed); }}
          onBlur={() => setHexText(null)} onKeyDown={event => { if (event.key === 'Enter' && hexValid) { event.preventDefault(); done(); } }} /></label>
    </div>
    {slider('h', t('#CustomColourHue'), 360, '°', 'linear-gradient(90deg,#f00,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,#f00)')}
    {slider('s', t('Saturation'), 100, '%', `linear-gradient(90deg,hsl(${colour.h} 0% ${colour.l}%),hsl(${colour.h} 100% ${colour.l}%))`)}
    {slider('l', t('Brightness'), 100, '%', `linear-gradient(90deg,#000,hsl(${colour.h} ${colour.s}% 50%),#fff)`)}
    {slider('a', t('Transparency'), 100, '%', `linear-gradient(90deg,${css({ ...colour, a: 1 })},transparent),repeating-conic-gradient(#d9d9d4 0 25%,#fff 0 50%) 0 0/10px 10px`,
      Math.round((1 - colour.a) * 100), next => change({ a: Math.max(0, Math.min(1, 1 - next / 100)) }))}
  </div>;
}
